/*
  Results-page chrome: facets, sort and the result count.

  Everything here belongs to the page around the cards rather than to a card:

    1. shapeOptions    — which facets the app is allowed to render, and in which language
    2. revealSoon      — the CLS reveal: facets are hidden until their DOM stops changing
    3. ensureSortLabel — the custom "Sort: <value>" label over the app's bare <select>
    4. statsTemplate   — "583 results" instead of the app's "1 - 72 out of 583 results found in …"

  Locale-specific facet lists are an index-side convention: the Algolia records carry both an
  English and an Arabic copy of every text facet, the Arabic ones suffixed `_ar`. Vendor is
  shared (brand names aren't translated). The app has no notion of that, so we filter its
  facet list per locale.
*/
window.PZSearch = window.PZSearch || {}

;(function (PZSearch) {
  'use strict'

  /*
    Long value lists in the filter panel (PHARM-1044): the first few values, then "Show more". A
    searchable facet shows fewer, because its search box reaches the rest. The collection page's
    panel uses the same two numbers — snippets/collection-mobile-filters.liquid; keep them in step.
  */
  const VISIBLE_VALUES = 10
  const VISIBLE_VALUES_SEARCHABLE = 5
  // The panel's breakpoint (filters.mobile-panel.css). The desktop column keeps its scrolling lists.
  const PANEL_MEDIA_QUERY = '(max-width: 767px)'

  // Reveal the facets this long after their DOM last changed…
  const QUIET_MS = 300
  // …but never keep them hidden longer than this, whatever the app is doing.
  const HARD_CAP_MS = 3500

  function isArabicFacet(name) {
    return String(name).includes('_ar')
  }

  function isVendorFacet(name) {
    return String(name).includes('vendor')
  }

  /*
    `meta.custom.search_category_lvl0/1/2` are the category hierarchy Algolia's Query
    Categorization needs (PHARM-978), which means they have to sit in the index's
    attributesForFaceting — and the Shopify app only puts a facet there once it is *enabled* in
    the app, which also renders it. They are machine-readable input, not something a customer
    should filter on, so the app's widgets for them are dropped here.
  */
  function isSearchCategoryFacet(name) {
    return String(name).startsWith('meta.custom.search_category_lvl')
  }

  /*
    Filter and relabel the facet/sort options the app block is about to render.

    Behaviour matches the old theme; the difference is that the labels arrive as a map in the
    data island instead of a Liquid-interpolated switch statement, so adding a translated sort
    order is a data change.
  */
  function shapeOptions(options, config) {
    if (!options) return options

    // The app defaults to "and" between values of the same facet, which on a store this size
    // means most second clicks return nothing.
    if (options.facets && options.facets.widgets) {
      options.facets.widgets.forEach(function (widget) {
        widget.params.operator = 'or'
      })
    }

    if (options.facets) {
      const keepLocaleFacet = config.isArabic
        ? function (name) {
            return isArabicFacet(name) || isVendorFacet(name)
          }
        : function (name) {
            return !isArabicFacet(name)
          }

      // One predicate for all three lists below, so a facet can't be hidden from the list but
      // left in the widgets.
      const keepFacet = function (name) {
        return !isSearchCategoryFacet(name) && keepLocaleFacet(name)
      }

      if (options.facets.list) {
        options.facets.list = options.facets.list.filter(function (facet) {
          facet.title = facetTitle(facet, config)
          return keepFacet(facet.name)
        })
      }

      if (options.facets.shown) {
        options.facets.shown = options.facets.shown.filter(function (facet) {
          return keepFacet(facet.name)
        })
      }

      if (options.facets.widgets) {
        options.facets.widgets = options.facets.widgets.filter(function (widget) {
          return keepFacet(widget.facet.name)
        })
      }
    }

    // Long value lists fold behind "Show more" — see foldLongList. Decided once, as the widgets are
    // built once.
    if (options.facets && options.facets.widgets && window.matchMedia(PANEL_MEDIA_QUERY).matches) {
      options.facets.widgets.forEach(function (widget) {
        if (widget.name === 'refinementList') foldLongList(widget, config)
      })
    }

    /*
      Sort orders are configured in the Algolia dashboard, so their labels come back in English
      whatever the storefront locale is. The map is keyed by those English labels.
    */
    if (options.sortOrders && config.sortLabels) {
      options.sortOrders.forEach(function (order) {
        const translated = config.sortLabels[order.label]
        if (translated) order.label = translated
      })
    }

    if (options.sortOrders) retargetSortOrders(options.sortOrders, config.indexName)

    return options
  }

  /*
    InstantSearch's own show-more on a refinement list: `limit` values first, up to the app's
    original limit once expanded. The search box follows the facet's "searchable" setting in the
    app, which also makes the index searchable on that attribute — switching it on here alone would
    fail every query (`searchable(vendor)` missing from attributesForFaceting, checked 2026-10-22).
  */
  function foldLongList(widget, config) {
    const params = widget.params
    const searchable = Boolean(widget.facet && widget.facet.searchable)
    const visible = searchable ? VISIBLE_VALUES_SEARCHABLE : VISIBLE_VALUES
    const all = params.limit || 100
    if (all <= visible) return

    params.limit = visible
    params.showMore = true
    params.showMoreLimit = all
    params.templates = Object.assign({}, params.templates, {
      showMoreText: function (data) {
        return data.isShowingMore ? config.labels.showLess : '+ ' + config.labels.showMore
      }
    })

    // The search box's placeholder comes from searchablePlaceholder below, at every width.
    if (searchable) params.searchable = true
  }

  /*
    A facet's title as the customer reads it. Vendor is the one facet shown to both locales, so
    its title is the app's English "Brand" on /ar too.
  */
  function facetTitle(facet, config) {
    return config.isArabic && facet.title === 'Brand' && config.labels.brand ? config.labels.brand : facet.title
  }

  /*
    The value search inside a searchable facet. The app hardcodes "Search for <title>" and
    "No matching for <title>" in English; both go through its documented hooks, so /ar reads
    "ابحث في الماركة" on desktop as well as in the mobile panel.
  */
  function searchablePlaceholder(defaultPlaceholder, facet, config) {
    if (!facet || !config.labels.searchValues) return defaultPlaceholder
    return config.labels.searchValues.replace('%filter%', facetTitle(facet, config))
  }

  function searchableNoResults(defaultHtml, config) {
    if (!config.labels.noValues) return defaultHtml
    const div = document.createElement('div')
    div.textContent = config.labels.noValues
    return div.outerHTML
  }

  /*
    The app builds the sort options on the index it resolved from its markets mapping
    (`shopify_products` + suffix, then `_price_asc`…). When the data island picked another index,
    none of the options matches the current one: the select has nothing selected, our label never
    renders and the sort control disappears, and picking an option jumped to the other index's
    replicas (PHARM-1030, MENA Europe on /ar). Point them at our index's replicas instead.

    The app's base index is the option every other one starts with (the relevance entry).
  */
  function retargetSortOrders(sortOrders, indexName) {
    const values = sortOrders.map(function (order) {
      return order.value
    })
    const base = values.find(function (value) {
      return values.every(function (other) {
        return other === value || other.indexOf(value + '_') === 0
      })
    })
    if (!base || base === indexName) return

    sortOrders.forEach(function (order) {
      order.value = indexName + order.value.slice(base.length)
    })
  }

  /*
    The facets are `display: none` until `.ais-facets-ready` lands on `.ais-page` (see
    search.algolia.autocomplete.css). Without that they visibly re-flow several times while
    Algolia's progressive render fills in 1200+ facet values.

    "Settled" is tied to the facets' own DOM going quiet rather than to a render event, so the
    reveal isn't held back by later, unrelated hit or enrich re-renders. Until the facets
    element exists we watch the app's mount instead — otherwise a query with zero hits, where
    no card ever renders, would leave them hidden forever.
  */
  function createFacetsReveal() {
    let revealed = false
    let quietTimer = 0
    let hardCapTimer = 0
    let observer = null
    let observing = null // the element `observer` is attached to

    function reveal() {
      if (revealed) return
      revealed = true
      clearTimeout(quietTimer)
      clearTimeout(hardCapTimer)
      if (observer) {
        observer.disconnect()
        observer = null
      }
      const page = document.querySelector('.ais-page')
      if (page) page.classList.add('ais-facets-ready')
    }

    function bump() {
      if (revealed) return
      clearTimeout(quietTimer)
      quietTimer = setTimeout(reveal, QUIET_MS)
    }

    function observe(target) {
      if (observing === target) return
      if (observer) observer.disconnect()
      observer = new MutationObserver(function () {
        // Re-target as soon as the facets themselves exist, so hit re-renders stop counting.
        narrow()
        bump()
      })
      observer.observe(target, { childList: true, subtree: true })
      observing = target
    }

    function narrow() {
      const facets = document.querySelector('.ais-page .ais-facets')
      if (facets) observe(facets)
    }

    return function revealSoon() {
      if (revealed) return

      if (!window.MutationObserver) {
        reveal()
        return
      }

      const facets = document.querySelector('.ais-page .ais-facets')
      const mount = document.getElementById('algolia-shopify-instantsearch')
      const target = facets || mount
      if (!target) return

      observe(target)
      bump()
      if (!hardCapTimer) hardCapTimer = setTimeout(reveal, HARD_CAP_MS)
    }
  }

  /*
    The app renders sorting as a bare <select> showing only the current value. The old theme
    added a label over it — "Sort: Relevance" — with the value in a `data-text` attribute the
    CSS prints via ::after, because the <select> itself can't be styled that way.

    Idempotent: called from every render pass, so it doesn't need the old theme's
    setTimeout(1000) guess at when the app is done mounting.
  */
  function createSortLabel(config) {
    return function ensureSortLabel() {
      const container = document.querySelector('.ais-sort')
      const select = document.querySelector('.ais-SortBy-select')
      if (!container || !select) return

      const selected = select.options[select.selectedIndex]
      if (!selected) return

      const existing = container.querySelector('.ais-sort-custom-label__text')
      if (existing) {
        existing.textContent = config.translations.sortBy + ': ' + selected.text
        return
      }

      /*
        "Sort: <value>" as real text plus the chevron, rather than the old theme's
        `content: attr(data-text)` on a ::after. That trick made the value a pseudo-element, which
        can't be laid out predictably next to a sibling icon — the chevron ended up on its own line.
      */
      const label = document.createElement('span')
      label.className = 'ais-sort-custom-label'

      const text = document.createElement('span')
      text.className = 'ais-sort-custom-label__text'
      text.textContent = config.translations.sortBy + ': ' + selected.text
      label.appendChild(text)

      if (config.icons.chevron) label.insertAdjacentHTML('beforeend', config.icons.chevron)
      container.appendChild(label)

      select.addEventListener('change', function () {
        const current = select.options[select.selectedIndex]
        if (current) text.textContent = config.translations.sortBy + ': ' + current.text
      })
    }
  }

  /*
    Result count. The app's own stats line is "1 - 72 out of 583 results found in 0.002s"; the
    store shows just "583 results".

    The count templates come from the theme's pluralised `info.search_result_count` keys, which
    carry a literal `{{ count }}` placeholder because the number isn't known at render time. That
    covers one/other, i.e. every locale this store runs — a language with more plural forms would
    need the count resolved server-side.
  */
  function createStatsTemplate(config) {
    return function statsTemplate(_defaultTemplate, data, html) {
      const template = data.hasOneResult ? config.labels.resultCount.one : config.labels.resultCount.other
      const count = window.algoliaShopify.helpers.formatNumber(data.nbHits)
      const countText = template.replace('{{ count }}', count)

      setTitle(config, data, countText)

      if (!data.hasOneResult && !data.hasManyResults) return html``

      return html`<span class="ais-stats--nb-results">${countText}</span>`
    }
  }

  /*
    The tab title. Just before this template runs, the app's stats widget sets
    `document.title` to `Search: ${nbHits} for "${query}"` — hardcoded English, so the Arabic
    results page had an English tab (PHARM-1005). Written back in the storefront's language:
    'Search results for: "cetaphil" (53 results) – Pharmazone'.
  */
  function setTitle(config, data, countText) {
    const labels = config.labels
    // The app only retitles its full results page; so does this.
    if (config.pageType !== 'search' || !labels.resultsFor || !data.query) return
    const count = data.hasOneResult || data.hasManyResults ? ` (${countText})` : ''
    const shop = labels.shopName ? ` \u2013 ${labels.shopName}` : ''
    document.title = `${labels.resultsFor} "${data.query}"${count}${shop}`
  }

  PZSearch.createFacets = function createFacets(config) {
    return {
      shapeOptions: function (options) {
        return shapeOptions(options, config)
      },
      revealSoon: createFacetsReveal(),
      ensureSortLabel: createSortLabel(config),
      statsTemplate: createStatsTemplate(config),
      searchablePlaceholder: function (defaultPlaceholder, facet) {
        return searchablePlaceholder(defaultPlaceholder, facet, config)
      },
      searchableNoResults: function (defaultHtml) {
        return searchableNoResults(defaultHtml, config)
      }
    }
  }
})(window.PZSearch)
